import { Pie } from '@visx/shape';

import { useTheme } from '@simplywallst/ui-core';

import { Arc } from './components/Arc';
import { Annotation } from './components/Annotation';

import {
  COLORS,
  HEIGHT,
  INNER_RADIUS,
  OUTER_RADIUS,
  OTHERS_LABEL,
} from './constants';

import { labelCollisionTest } from './utils';
import { Datum, Fill } from './types';

interface Props {
  data?: Datum[];
  width: number;
  selected?: number;
  onSelect?: (index: number) => void;
}

const empty = [{ label: OTHERS_LABEL, allocation: 1 }];

const useFill = () => {
  const theme = useTheme();
  return (label: string, index: number): Fill => {
    if (label === OTHERS_LABEL) return theme.color.darkGray;
    if (index === 0) return theme.color.chart01;
    return theme.color[COLORS[(index - 1) % COLORS.length]];
  };
};

export const DiversificationDonutChart = ({
  data,
  width,
  selected = 0,
  onSelect,
}: Props) => {
  const getFill = useFill();
  const isEmpty = typeof data === 'undefined';
  return (
    <svg height={HEIGHT} width="100%">
      <Pie
        data={data || empty}
        pieValue={(d) => d.allocation}
        innerRadius={INNER_RADIUS}
        outerRadius={OUTER_RADIUS}
        pieSort={({ label, allocation }, b) => {
          if (label === OTHERS_LABEL) return 1;
          return allocation > b.allocation ? -1 : 1;
        }}
      >
        {(pie) => {
          let lastRenderedLabelIndex = 0;

          return pie.arcs
            .sort((a, b) => {
              if (a.data.label === OTHERS_LABEL) return 1;
              return a.data.allocation > b.data.allocation ? -1 : 1;
            })
            .map((arc, index) => {
              const fill = getFill(arc.data.label, arc.index);

              const shouldRenderLabel =
                isEmpty === false &&
                labelCollisionTest({
                  thisArc: arc,
                  lastArc: pie.arcs[lastRenderedLabelIndex],
                  pie,
                });

              if (shouldRenderLabel) {
                lastRenderedLabelIndex = index;
              }

              return (
                <g
                  key={`${arc.data.label}-${arc.index}`}
                  transform={`translate(${width / 2} ${HEIGHT / 2})`}
                >
                  {shouldRenderLabel && (
                    <Annotation pie={pie} arc={arc} fill={fill} width={width} />
                  )}
                  <Arc
                    key={`pie-arc-${arc.data.label}-${arc.index}`}
                    pie={pie}
                    arc={arc}
                    fill={fill}
                    selected={selected}
                    onSelect={(index) => onSelect?.(index)}
                  />
                </g>
              );
            });
        }}
      </Pie>
    </svg>
  );
};
